import { useState } from "react";
import {
  Autocomplete,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  autocompleteClasses,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { faMagnifyingGlass } from "@fortawesome/pro-light-svg-icons";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { Department } from "../../../type/department";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import AddOrganizerName from "./AddOrganizerName";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon sx={{ color: "#066993" }} fontSize="small" />;

const Organizer: React.FC<{
  selectOptions: {
    department: Department[];
    grp: Department[];
    ins: Department[];
    ind: Department[];
    fml: Department[];
  };
  organizer: { type: string; collaborators: Department[] };
  index: number;
  addOrganizer: (type: string, value: Department) => void;
  typeChange: (index: number, value: string) => void;
  nameChange: (index: number, value: Department[]) => void;
  showError: boolean;
  selectedTypes: string[];
}> = ({
  selectOptions,
  organizer,
  index,
  addOrganizer,
  typeChange,
  nameChange,
  showError,
  selectedTypes,
}) => {
  const [addCollaboratorName, isAddCollaboratorName] = useState<boolean>(false);
  const { t } = useTranslation();

  const addNewOrganizerName = (type: string, value: Department) => {
    addOrganizer(type, value);
    const name = [...organizer.collaborators, value];
    nameChange(index, name);
  };
  const filteredOptions = [
    { value: "grp", label: t("Register.Group") },
    { value: "ind", label: t("Register.Individual") },
    { value: "ins", label: t("Register.Institution") },
    { value: "fml", label: t("Register.Family") },
  ].filter(
    (option) =>
      !selectedTypes.includes(option.value) || option.value === organizer.type
  );

  const getOptionsByType = (type: string) => {
    switch (type) {
      case "ind":
        return selectOptions.ind;
      case "grp":
        return selectOptions.grp;
      case "ins":
        return selectOptions.ins;
      case "fml":
        return selectOptions.fml;
      default:
        return [];
    }
  };

  return (
    <div className="flex flex-wrap justify-between mt-5">
      <div className="w-full sm:w-1/2 md:pr-2">
        <p className="text-sm text-default-text mb-[6px]">
          {t("Tree Species.Type")} *
        </p>
        <FormControl style={{ width: "100%" }}>
          <InputLabel shrink={false}>
            {organizer.type === "" && t("Tree Species.Type")}
          </InputLabel>
          <Select
            sx={{
              border: "1px solid #eeecec",
              borderRadius: "4px",
              "& fieldset": { border: "none" },
            }}
            style={{ height: "48px" }}
            IconComponent={ExpandMoreIcon}
            variant="outlined"
            value={organizer.type}
            onChange={(e) => {
              const newType = e.target.value;
              typeChange(index, newType);
            }}
          >
            {filteredOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {showError && !organizer.type && (
          <p className="error-text">{t("Message.Required field")}</p>
        )}
      </div>
      <div className="w-full sm:w-1/2 sm:pl-2 mt-7 sm:mt-0">
        <p className="text-sm text-default-text mb-[6px]">
          {t("Tree Species.Name")} *
        </p>

        <Autocomplete
          popupIcon={
            <FontAwesomeIcon className="w-4 h-4" icon={faMagnifyingGlass} />
          }
          // @ts-ignore
          multiple={true}
          limitTags={1}
          id="checkboxes-tags-demo"
          options={getOptionsByType(organizer.type)}
          disableCloseOnSelect
          isOptionEqualToValue={(option: any, value: any) =>
            option.slug === value.slug
          }
          getOptionLabel={(option: any) => option.name}
          renderOption={(prop, option: any, { selected }) => (
            <li {...prop} key={option.slug}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {option.name}
            </li>
          )}
          sx={{
            width: "100%",
            [`& .${autocompleteClasses.popupIndicator}`]: {
              transform: "none",
            },
            "& .Mui-focused": {
              outline: "1px solid #439FC6",
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              sx={{
                border: "1px solid #eeecec",
                borderRadius: "4px",
                "& fieldset": { border: "none" },
              }}
              placeholder={
                organizer.collaborators?.length === 0
                  ? t("Tree Species.Name") + ""
                  : ""
              }
            />
          )}
          PaperComponent={({ children, ...rest }) => (
            <Paper
              elevation={3}
              style={{
                overflow: "hidden",
                borderRadius: "4px",
                border: "1px solid #eeecec",
              }}
            >
              <div className="px-4 py-2 border-b border-ph-iborder">
                <button
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    isAddCollaboratorName(true);
                  }}
                  className="text-sm w-full items-center flex justify-between text-ph-toggle-text-blue py-2"
                >
                  {t("Plantings.Create organizer")}
                  <FontAwesomeIcon className="ml-2" icon={faPlus} />
                </button>
              </div>
              <div>{children}</div>
            </Paper>
          )}
          // @ts-ignore
          onChange={(event, newValue) => {
            nameChange(index, newValue);
          }}
          value={organizer.collaborators}
        />
        {showError &&
          organizer.collaborators &&
          organizer.collaborators.length === 0 && (
            <p className="error-text">{t("Message.Required field")}</p>
          )}
      </div>
      {addCollaboratorName && (
        <AddOrganizerName
          addOrganizer={addNewOrganizerName}
          closeAddOrganizer={() => {
            isAddCollaboratorName(false);
          }}
          organizerType={organizer.type}
          openModal={addCollaboratorName}
        />
      )}
    </div>
  );
};

export default Organizer;
